import * as React from "react";
import {PremiumType, UPFRONT_PAYMENT_THRESHOLD} from "@ferry/journey-services";
import {getAmountWithDecimalDigits} from "../../../../../../CommonFunctions";
import {MtaSuccessModalWording} from "./MtaSuccessModalWording";
import {IFactors} from "./GeneratorInputTypes";
import * as successModals from "../../../../../../components/modals/SuccessModalRenderers";

export class EditCarMtaSuccessModalWordingGenerator {

    public static readonly generateModalWording = (factors: IFactors, paymentDetails: any,
                                                   payFullPrice: boolean, mtaDetailsWording: string | null, cardNumber: string,
                                                   agreeToReuseCardDetails: boolean | null, previousMonthlyPremium: any, renewalPremium: any): MtaSuccessModalWording => {
        const premiumType: PremiumType = PremiumType[factors.premiumType as keyof typeof PremiumType];
        const instalmentsNotPaidOff: boolean = factors.premiumType === PremiumType.monthly && paymentDetails.instalmentPlan.totalAmount > 0;

        const instalmentPlanMonthlyPremium = paymentDetails.instalmentPlan!.monthlyPayment;
        return new MtaSuccessModalWording(
            EditCarMtaSuccessModalWordingGenerator.generateModalTitle(factors, premiumType, payFullPrice, instalmentsNotPaidOff),
            EditCarMtaSuccessModalWordingGenerator.generateModalBody(factors, instalmentPlanMonthlyPremium, premiumType,
                mtaDetailsWording, payFullPrice, cardNumber, agreeToReuseCardDetails, previousMonthlyPremium, instalmentsNotPaidOff, renewalPremium),
            EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle(),
            EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody())

    }

    public static readonly generateModalTitle = (factors: IFactors, premiumType: PremiumType, payFullPrice: boolean, instalmentsNotPaidOff: boolean): React.ReactNode => {
        const {additionalPremium, amountToRefund} = factors;

        if (additionalPremium > 0) {

            switch (premiumType) {
                case PremiumType.annual:
                    return "Payment was successful";
                case PremiumType.monthly:
                    return payFullPrice ? "Payment was successful" : "Payment was successfully added to your payment plan";
                default:
                    return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
            }

        } else if (amountToRefund > 0) {

            switch (premiumType) {
                case PremiumType.annual:
                    return "Your refund is on the way";
                case PremiumType.monthly:
                    if (instalmentsNotPaidOff) {
                        return "Your payment plan has been updated";
                    } else {
                        return "Your refund is on the way";
                    }
                default:
                    return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
            }

        } else {
            return EditCarMtaSuccessModalWordingGenerator.defaultModalTitle;
        }
    }

    public static generateModalBody = (factors: IFactors, instalmentPlanMonthlyPremium: number | null,
                                       premiumType: PremiumType, mtaDetailsWording: string | null, payFullPrice: boolean,
                                       cardNumber: string, agreeToReuseCardDetails: boolean | null, previousMonthlyPremium: any, instalmentsNotPaidOff: boolean, renewalPremium: any): React.ReactNode => {

        const {
            additionalPremium,
            amountToRefund
        } = factors;

        if (additionalPremium > 0) {
            return EditCarMtaSuccessModalWordingGenerator.getAdditionalPremiumWording(factors,
                premiumType, mtaDetailsWording, payFullPrice,
                cardNumber, agreeToReuseCardDetails, previousMonthlyPremium, renewalPremium);
        } else if (amountToRefund > 0) {
            return EditCarMtaSuccessModalWordingGenerator.getRefundWording(factors, premiumType, mtaDetailsWording, instalmentPlanMonthlyPremium, previousMonthlyPremium, instalmentsNotPaidOff, renewalPremium);
        } else {
            return (
                <div>
                    <p className="font-weight-extra-light">
                        There is no extra cost for the changes you have made.
                    </p>
                    {renewalPremium &&
                        (<div className="font-weight-semi-bold">
                            <p>
                                Your next year's renewal price will
                                be {getAmountWithDecimalDigits(renewalPremium)}&nbsp;
                            </p>
                            <p>
                                Please check your renewal tile for further details
                            </p>
                        </div>)
                    }
                </div>
            )
        }
    }

    private static getAdditionalPremiumWording = (factors: IFactors,
                                                  premiumType: PremiumType, mtaDetailsWording: string | null, payFullPrice: boolean,
                                                  cardNumber: string, agreeToReuseCardDetails: boolean | null, previousMonthlyPremium: any, renewalAmount: any): React.ReactNode => {

        const {
            additionalPremium,
            monthlyPremium,
            upfrontPaymentAmount,
            monthlyPaymentSpreadAmount
        } = factors;

        const wordingToShow = mtaDetailsWording ?? "for the changes you have made";

        if (additionalPremium > 0 && (premiumType === PremiumType.annual || payFullPrice) && cardNumber) {
            const modalContent = (<>{wordingToShow}.<br/></>)
            return successModals.paymentSuccessfulAdditionalPremiumModal(factors.additionalPremium, cardNumber, modalContent, agreeToReuseCardDetails, renewalAmount);
        } else if (additionalPremium > 0 && premiumType === PremiumType.monthly && upfrontPaymentAmount >= UPFRONT_PAYMENT_THRESHOLD && !payFullPrice && cardNumber) {
            const modalContent = (<>{wordingToShow}.<br/></>)
            return successModals.paymentSuccessfulAdditionalPremiumMonthlyModal(upfrontPaymentAmount, monthlyPaymentSpreadAmount, cardNumber, modalContent, agreeToReuseCardDetails, renewalAmount);
        } else if (additionalPremium > 0 && premiumType === PremiumType.monthly && upfrontPaymentAmount < UPFRONT_PAYMENT_THRESHOLD && !payFullPrice) {
            return successModals.paymentPlanUpdatedAdditionalPremiumModal(monthlyPaymentSpreadAmount, monthlyPremium, previousMonthlyPremium, renewalAmount);
        }

        return "";
    }

    private static getRefundWording = (factors: IFactors, premiumType: PremiumType, mtaDetailsWording: string | null,
                                       instalmentPlanMonthlyPremium: number | null, previousMonthlyPremium: any, instalmentsNotPaidOff: boolean, renewalPremium: any): React.ReactNode => {

        const {
            amountToRefund,
            monthlyPremium
        } = factors;

        const wordingToShow = mtaDetailsWording ?? "for the changes you have made";

        if (amountToRefund > 0 && premiumType === PremiumType.annual) {
            return (
                <div>
                    <p className="font-weight-extra-light">
                        {getAmountWithDecimalDigits(amountToRefund)} has been returned {" "}
                        {wordingToShow}
                        .
                    </p>
                    {renewalPremium &&
                        (<div className="font-weight-semi-bold">
                            <p>
                                Your next year's renewal price will
                                be {getAmountWithDecimalDigits(renewalPremium)}&nbsp;
                            </p>
                            <p>
                                Please check your renewal tile for further details
                            </p>
                        </div>)
                    }
                </div>
            )
        } else if (amountToRefund > 0 && premiumType === PremiumType.monthly) {
            const monthlyPremiumToShow: number = instalmentPlanMonthlyPremium === null ? monthlyPremium : instalmentPlanMonthlyPremium;
            return successModals.paymentPlanUpdatedRefundModal(amountToRefund, monthlyPremiumToShow, previousMonthlyPremium, instalmentsNotPaidOff, renewalPremium);
        }
        return "";
    }

    public static generateAnythingElseChangedModalTitle = (): React.ReactNode => {
        return EditCarMtaSuccessModalWordingGenerator.anythingElseChangedModalTitle;
    }

    public static generateAnythingElseChangedModalBody = (): React.ReactNode => {
        return EditCarMtaSuccessModalWordingGenerator.anythingElseChangedModalBody;
    }

    private static defaultModalTitle: string = "Change was successful";
    private static anythingElseChangedModalTitle: string = "Has anything else changed?";
    private static anythingElseChangedModalBody: string = "Please check everything’s still accurate and make any changes required so you can be sure all policy details are up to date.";
}
